import React, { useState, useEffect } from 'react';
import { 
  Users, Search, Edit, UserPlus, UserX, User, CheckCircle, Image, 
  Phone, Home, Briefcase, XCircle
} from 'lucide-react';
import { api } from '../utils/api';

const academicPositions = [
  'ครูผู้ช่วย',
  'ครู',
  'ครูชำนาญการ',
  'ครูชำนาญการพิเศษ',
  'ครูเชี่ยวชาญ',
  'ครูเชี่ยวชาญพิเศษ',
  'ครูอัตราจ้าง',
  'ลูกจ้าง(ชั่วคราว)',
];

const EmployeeManagement = ({ user }) => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  
  const [newEmployee, setNewEmployee] = useState({
    employee_id: '',
    national_id: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    position: '',
    academic_position: '',
    department: '',
    organization_id: user?.organization_id || 1, 
    save_attendance_image: false
  });

  const isStaff = user?.is_staff === true;
  const isAdmin = user?.is_admin === true;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let filtered = employees;

    if (isStaff) {
      filtered = filtered.filter(emp => 
        emp.employee_id !== 'ADMIN' && emp.position !== 'ผู้ดูแลระบบ'
      );
    }

    if (statusFilter === 'active') {
      filtered = filtered.filter(emp => emp.is_active == 1);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(emp => emp.is_active == 0);
    }

    if (searchTerm) {
      filtered = filtered.filter(emp =>
        emp.employee_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (emp.department && emp.department.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.position && emp.position.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.academic_position && emp.academic_position.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.phone && emp.phone.includes(searchTerm))
      );
    }

    setFilteredEmployees(filtered);
  }, [employees, searchTerm, statusFilter, isStaff]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [empData, deptData] = await Promise.all([
        api.getEmployees(),
        api.getDepartments()
      ]);

      const sortedEmpData = empData.sort((a, b) => {
        const activeCompare = b.is_active - a.is_active;
        if (activeCompare !== 0) {
          return activeCompare;
        }

        const deptA = a.department || '';
        const deptB = b.department || '';
        
        const deptCompare = deptA.localeCompare(deptB);
        if (deptCompare !== 0) {
          return deptCompare;
        }
        
        return a.employee_id.localeCompare(b.employee_id);
      });
      
      setEmployees(sortedEmpData);
      setDepartments(deptData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEmployee = async () => {
    try {
      const employeeData = {
        ...newEmployee,
        organization_id: user?.organization_id || 1,
        save_attendance_image: isAdmin ? newEmployee.save_attendance_image : true
      };

      const data = await api.addEmployee(employeeData);

      if (data.success) {
        setShowAddModal(false);
        setNewEmployee({
          employee_id: '',
          national_id: '',
          first_name: '',
          last_name: '',
          phone: '',
          address: '',
          position: '',
          academic_position: '',
          department: '',
          organization_id: user?.organization_id || 1,
          save_attendance_image: true
        });
        loadData();
        window.alert('เพิ่มบุคลากรเรียบร้อยแล้ว');
      } else {
        window.alert('ไม่สามารถเพิ่มบุคลากรได้: ' + data.error);
      }
    } catch (error) {
      console.error('Error adding employee:', error);
      window.alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleEditEmployee = async () => {
    try {
      const updateData = {
        employee_id: selectedEmployee.employee_id,
        national_id: selectedEmployee.national_id,
        first_name: selectedEmployee.first_name,
        last_name: selectedEmployee.last_name,
        phone: selectedEmployee.phone,
        address: selectedEmployee.address,
        position: selectedEmployee.position,
        academic_position: selectedEmployee.academic_position,
        department: selectedEmployee.department,
        is_active: selectedEmployee.is_active
      };
  
      if (!isStaff && selectedEmployee.save_attendance_image !== undefined) {
        updateData.save_attendance_image = selectedEmployee.save_attendance_image;
      }
  
      const data = await api.updateEmployee(updateData);
  
      if (data.success) {
        setShowEditModal(false);
        setSelectedEmployee(null);
        loadData();
        window.alert('แก้ไขข้อมูลบุคลากรเรียบร้อยแล้ว');
      } else {
        window.alert('ไม่สามารถแก้ไขข้อมูลได้: ' + data.error);
      }
    } catch (error) {
      console.error('Error updating employee:', error);
      window.alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleDeactivateEmployee = async (employeeId, firstName, lastName) => {
    if (!window.confirm(`ต้องการปิดการใช้งานของ ${firstName} ${lastName} หรือไม่?\n\nการดำเนินการนี้จะทำให้บุคลากรไม่สามารถใช้งานระบบได้`)) {
      return;
    }

    try {
      const data = await api.deactivateEmployee(employeeId);

      if (data.success) {
        await loadData();
        window.alert('ปิดการใช้งานบุคลากรเรียบร้อยแล้ว');
      } else {
        window.alert('ไม่สามารถปิดการใช้งานได้: ' + data.error);
      }
    } catch (error) {
      console.error('Error deactivating employee:', error);
      window.alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleReactivateEmployee = async (employeeId, firstName, lastName) => {
    if (!window.confirm(`ต้องการเปิดการใช้งานของ ${firstName} ${lastName} หรือไม่?`)) {
      return;
    }

    try {
      const currentEmployee = employees.find(emp => emp.employee_id === employeeId);
      const updatedEmployee = {
        ...currentEmployee,
        is_active: 1
      };
      
      const data = await api.updateEmployee(updatedEmployee);

      if (data.success) {
        await loadData();
        window.alert('เปิดการใช้งานบุคลากรเรียบร้อยแล้ว');
      } else {
        window.alert('ไม่สามารถเปิดการใช้งานได้: ' + data.error);
      }
    } catch (error) {
      console.error('Error reactivating employee:', error);
      window.alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <h1 className="text-3xl font-bold text-gray-900">จัดการบุคลากร</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-500 text-white px-6 py-3 rounded-md hover:bg-blue-600 flex items-center space-x-2 transition-colors"
        >
          <UserPlus className="h-5 w-5" />
          <span>เพิ่มบุคลากร</span>
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <h2 className="text-lg font-semibold text-gray-900">
              รายชื่อบุคลากร ({filteredEmployees.length} คน)
            </h2>
            
            <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">ทุกสถานะ</option>
                <option value="active">ใช้งานอยู่</option>
                <option value="inactive">ปิดการใช้งาน</option>
              </select>

              <div className="relative">
                <Search className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="ค้นหาบุคลากร..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full sm:w-64"
                />
              </div>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">รหัส</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ชื่อ-นามสกุล</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เบอร์โทรศัพท์</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ตำแหน่ง</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">แผนก/กลุ่มสาระฯ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะอุปกรณ์ (Passkey)</th>
                {isAdmin && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">เก็บภาพ</th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">สถานะการใช้งาน</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">การจัดการ</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEmployees.map((employee) => (
                <tr key={employee.employee_id} className={`hover:bg-gray-50 ${employee.is_active == 0 ? 'opacity-60' : ''} ${employee.employee_id === 'ADMIN' || employee.position === 'ผู้ดูแลระบบ' ? 'bg-blue-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {employee.employee_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {employee.first_name} {employee.last_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {employee.phone || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {employee.academic_position || employee.position || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {employee.department || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {parseInt(employee.has_passkey) > 0 ? (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                        <CheckCircle className="h-4 w-4 mr-1" /> ลงทะเบียนแล้ว
                      </span>
                    ) : (
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                        <XCircle className="h-4 w-4 mr-1" /> ยังไม่ลงทะเบียน
                      </span>
                    )}
                  </td>
                  {isAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        employee.save_attendance_image == 1
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {employee.save_attendance_image == 1 ? 'เก็บ' : 'ไม่เก็บ'}
                      </span>
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      employee.is_active == 1
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {employee.is_active == 1 ? 'ใช้งานอยู่' : 'ปิดการใช้งาน'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      {employee.employee_id !== 'ADMIN' && employee.position !== 'ผู้ดูแลระบบ' ? (
                        <>
                          <button
                            onClick={() => {
                              setSelectedEmployee({
                                ...employee,
                                phone: employee.phone || '',
                                address: employee.address || '',
                                academic_position: employee.academic_position || ''
                              });
                              setShowEditModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="แก้ไขข้อมูล"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          
                          {employee.is_active == 1 ? (
                            <button
                              onClick={() => handleDeactivateEmployee(employee.employee_id, employee.first_name, employee.last_name)}
                              className="text-red-600 hover:text-red-900 transition-colors"
                              title="ปิดการใช้งาน"
                            >
                              <UserX className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleReactivateEmployee(employee.employee_id, employee.first_name, employee.last_name)}
                              className="text-green-600 hover:text-green-900 transition-colors"
                              title="เปิดการใช้งาน"
                            >
                              <User className="h-4 w-4" />
                            </button>
                          )}
                        </>
                      ) : (
                        <span className="text-xs text-gray-400 italic">ผู้ดูแลระบบ</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredEmployees.length === 0 && (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">ไม่พบบุคลากร</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' 
                ? 'ไม่มีบุคลากรที่ตรงกับเงื่อนไขการค้นหา'
                : 'เริ่มต้นด้วยการเพิ่มบุคลากรคนแรก'}
            </p>
          </div>
        )}
      </div>

      {/* Add Employee Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-6">เพิ่มบุคลากรใหม่</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">รหัสบุคลากร *</label>
                <input
                  type="text"
                  value={newEmployee.employee_id}
                  onChange={(e) => setNewEmployee({...newEmployee, employee_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น EMP001"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">เลขบัตรประชาชน *</label>
                <input
                  type="text"
                  value={newEmployee.national_id}
                  onChange={(e) => setNewEmployee({...newEmployee, national_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="13 หลัก"
                  maxLength="13"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อ *</label>
                  <input
                    type="text"
                    value={newEmployee.first_name}
                    onChange={(e) => setNewEmployee({...newEmployee, first_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">นามสกุล *</label>
                  <input
                    type="text"
                    value={newEmployee.last_name}
                    onChange={(e) => setNewEmployee({...newEmployee, last_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <span>เบอร์โทรศัพท์</span>
                  </div>
                </label>
                <input
                  type="tel"
                  value={newEmployee.phone}
                  onChange={(e) => setNewEmployee({...newEmployee, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น 0812345678"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Home className="h-4 w-4 text-gray-500" />
                    <span>ที่อยู่ (ย่อ)</span>
                  </div>
                </label>
                <textarea
                  rows="2"
                  value={newEmployee.address}
                  onChange={(e) => setNewEmployee({...newEmployee, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="เช่น 123/45 ต.บางใหญ่ อ.บางใหญ่ จ.นนทบุรี"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ตำแหน่ง (Role) *</label>
                <select
                  value={newEmployee.position}
                  onChange={(e) => setNewEmployee({...newEmployee, position: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">เลือกตำแหน่ง</option>
                  {isAdmin && <option value="ผู้ดูแลระบบ">ผู้ดูแลระบบ</option>}
                  <option value="เจ้าหน้าที่">เจ้าหน้าที่</option>
                  <option value="บุคลากร">บุคลากร</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Briefcase className="h-4 w-4 text-gray-500" />
                    <span>ตำแหน่งทางวิชาการ</span>
                  </div>
                </label>
                <select
                  value={newEmployee.academic_position}
                  onChange={(e) => setNewEmployee({...newEmployee, academic_position: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- ไม่ระบุ --</option>
                  {academicPositions.map(pos => (
                    <option key={pos} value={pos}>
                      {pos}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">แผนก/กลุ่มสาระฯ</label>
                <select
                  value={newEmployee.department}
                  onChange={(e) => setNewEmployee({...newEmployee, department: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- ไม่ระบุ --</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.department_name}>
                      {dept.department_name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  (หากไม่มีแผนกที่ต้องการ กรุณาไปที่หน้า "จัดการแผนก" ก่อน)
                </p>
              </div>

              {isAdmin && (
                <div className="pt-2 border-t">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newEmployee.save_attendance_image}
                      onChange={(e) => setNewEmployee({
                        ...newEmployee, 
                        save_attendance_image: e.target.checked
                      })}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div>
                      <div className="flex items-center space-x-2">
                        <Image className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">
                          เก็บภาพเวลาสแกนหน้า
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        บันทึกภาพถ่ายเมื่อบุคลากรคนนี้สแกนหน้าลงเวลา
                      </p>
                    </div>
                  </label>
                </div>
              )}

            </div>

            <div className="flex space-x-4 mt-8">
              <button
                onClick={handleAddEmployee}
                disabled={!newEmployee.employee_id || !newEmployee.national_id || !newEmployee.first_name || !newEmployee.last_name || !newEmployee.position}
                className="flex-1 bg-blue-500 text-white py-3 px-4 rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                เพิ่มบุคลากร
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Employee Modal */}
      {showEditModal && selectedEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 mb-6">แก้ไขข้อมูลบุคลากร</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">รหัสบุคลากร</label>
                <input
                  type="text"
                  value={selectedEmployee.employee_id}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">เลขบัตรประชาชน *</label>
                <input
                  type="text"
                  value={selectedEmployee.national_id}
                  onChange={(e) => setSelectedEmployee({...selectedEmployee, national_id: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  maxLength="13"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อ *</label>
                  <input
                    type="text"
                    value={selectedEmployee.first_name}
                    onChange={(e) => setSelectedEmployee({...selectedEmployee, first_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">นามสกุล *</label>
                  <input
                    type="text"
                    value={selectedEmployee.last_name}
                    onChange={(e) => setSelectedEmployee({...selectedEmployee, last_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <span>เบอร์โทรศัพท์</span>
                  </div>
                </label>
                <input
                  type="tel"
                  value={selectedEmployee.phone || ''}
                  onChange={(e) => setSelectedEmployee({...selectedEmployee, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Home className="h-4 w-4 text-gray-500" />
                    <span>ที่อยู่ (ย่อ)</span>
                  </div>
                </label>
                <textarea
                  rows="2"
                  value={selectedEmployee.address || ''}
                  onChange={(e) => setSelectedEmployee({...selectedEmployee, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">ตำแหน่ง (Role) *</label>
                <select
                  value={selectedEmployee.position || ''}
                  onChange={(e) => setSelectedEmployee({...selectedEmployee, position: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">เลือกตำแหน่ง</option>
                  {isAdmin && <option value="ผู้ดูแลระบบ">ผู้ดูแลระบบ</option>}
                  <option value="เจ้าหน้าที่">เจ้าหน้าที่</option>
                  <option value="บุคลากร">บุคลากร</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center space-x-2">
                    <Briefcase className="h-4 w-4 text-gray-500" />
                    <span>ตำแหน่งทางวิชาการ</span>
                  </div>
                </label>
                <select
                  value={selectedEmployee.academic_position || ''}
                  onChange={(e) => setSelectedEmployee({...selectedEmployee, academic_position: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- ไม่ระบุ --</option>
                  {academicPositions.map(pos => (
                    <option key={pos} value={pos}>
                      {pos}
                    </option>
                  ))}
                </select>
              </div>

              {isAdmin && (
                <div className="pt-2 border-t">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedEmployee.save_attendance_image != 0}
                      onChange={(e) => setSelectedEmployee({
                        ...selectedEmployee, 
                        save_attendance_image: e.target.checked ? 1 : 0
                      })}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div>
                      <div className="flex items-center space-x-2">
                        <Image className="h-4 w-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">
                          เก็บภาพเวลาสแกนหน้า
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        บันทึกภาพถ่ายเมื่อบุคลากรคนนี้สแกนหน้าลงเวลา
                      </p>
                    </div>
                  </label>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">แผนก/กลุ่มสาระฯ</label>
                <select
                  value={selectedEmployee.department || ''}
                  onChange={(e) => setSelectedEmployee({...selectedEmployee, department: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">-- ไม่ระบุ --</option>
                  {departments.map(dept => (
                    <option key={dept.id} value={dept.department_name}>
                      {dept.department_name}
                    </option>
                  ))}
                </select>
              </div>

            </div>

            <div className="flex space-x-4 mt-8">
              <button
                onClick={handleEditEmployee}
                className="flex-1 bg-blue-500 text-white py-3 px-4 rounded-md hover:bg-blue-600 transition-colors"
              >
                บันทึกการแก้ไข
              </button>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedEmployee(null);
                }}
                className="flex-1 bg-gray-300 text-gray-700 py-3 px-4 rounded-md hover:bg-gray-400 transition-colors"
              >
                ยกเลิก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;